import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Plus, Pencil, Trash2, Code, RefreshCw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DEFAULT_INDICATOR_TITLE, DEFAULT_PINE_SCRIPT } from "@/lib/defaultIndicator";

type Indicator = {
  id: string; title: string; pine_script_code: string;
  is_active: boolean; created_at: string; updated_at: string;
};

type Props = { sessionToken: string };

const IndicatorTab = ({ sessionToken }: Props) => {
  const { toast } = useToast();
  const [indicators, setIndicators] = useState<Indicator[]>([]);
  const [loading, setLoading] = useState(false);
  const [editIndicator, setEditIndicator] = useState<Indicator | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCode, setEditCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCode, setNewCode] = useState("");

  const fetchIndicators = async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_indicators", sessionToken },
      });
      const fetched = res.data?.indicators || [];
      if (fetched.length === 0) {
        // Auto-seed the default indicator
        const seedRes = await supabase.functions.invoke("manage-certificates", {
          body: { action: "save_indicator", sessionToken, title: DEFAULT_INDICATOR_TITLE, pine_script_code: DEFAULT_PINE_SCRIPT },
        });
        if (seedRes.data?.success) {
          const res2 = await supabase.functions.invoke("manage-certificates", {
            body: { action: "list_indicators", sessionToken },
          });
          setIndicators(res2.data?.indicators || []);
          toast({ title: "Default indicator imported", description: "The current Pine Script from /indicator has been added." });
          setLoading(false);
          return;
        }
      }
      setIndicators(fetched);
    } catch {
      toast({ title: "Failed to load indicators", variant: "destructive" });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchIndicators(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "save_indicator", sessionToken,
          indicatorId: editIndicator?.id,
          title: editTitle, pine_script_code: editCode,
        },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Indicator updated" });
      setEditIndicator(null);
      fetchIndicators();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "save_indicator", sessionToken, title: newTitle, pine_script_code: newCode },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Indicator created" });
      setShowAdd(false); setNewTitle(""); setNewCode("");
      fetchIndicators();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_indicator", sessionToken, indicatorId: id },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      toast({ title: "Indicator deleted" });
      fetchIndicators();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };




  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Indicator Management</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowAdd(true); setNewTitle(""); setNewCode(""); }}>
            <Plus className="w-4 h-4 mr-1" /> Add Indicator
          </Button>
          <Button variant="outline" onClick={fetchIndicators} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Refresh</>}
          </Button>
        </div>
      </div>



      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : indicators.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Code className="w-10 h-10 mx-auto mb-3" />
          <p>No indicators yet. Add one to get started.</p>
          <p className="text-xs mt-1">The first indicator added will be displayed on the /indicator page.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground">All Indicators ({indicators.length})</h3>
          {indicators.map((ind) => (
             <Card key={ind.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{ind.title || "Untitled"}</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditIndicator(ind); setEditTitle(ind.title); setEditCode(ind.pine_script_code);
                    }}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="destructive"><Trash2 className="w-3 h-3" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete indicator?</AlertDialogTitle>
                          <AlertDialogDescription>This will permanently remove "{ind.title}".</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(ind.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <pre className="text-xs font-mono text-muted-foreground bg-muted/50 p-3 rounded-lg max-h-32 overflow-y-auto whitespace-pre-wrap">
                  {ind.pine_script_code.slice(0, 500)}{ind.pine_script_code.length > 500 ? "..." : ""}
                </pre>
                <p className="text-xs text-muted-foreground mt-2">Updated: {new Date(ind.updated_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Indicator</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Tick Volume + POC + CVD" />
            </div>
            <div className="space-y-2">
              <Label>Pine Script Code</Label>
              <Textarea value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="Paste Pine Script code here..." className="font-mono text-sm min-h-[300px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving || !newTitle.trim()}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editIndicator} onOpenChange={(open) => !open && setEditIndicator(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Indicator</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Pine Script Code</Label>
              <Textarea value={editCode} onChange={(e) => setEditCode(e.target.value)} className="font-mono text-sm min-h-[300px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIndicator(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndicatorTab;
